import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Eye, Calendar, Mail, TrendingUp } from "lucide-react";

interface Statistic {
  metric_name: string;
  metric_value: number;
}

const DashboardStats = () => {
  const [stats, setStats] = useState<Statistic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from('site_statistics')
        .select('metric_name, metric_value')
        .eq('date', new Date().toISOString().split('T')[0]);

      if (error) throw error;

      setStats(data || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatValue = (metricName: string) => {
    const stat = stats.find(s => s.metric_name === metricName);
    return stat?.metric_value || 0;
  };

  const statsCards = [
    {
      title: "Vistas de Página",
      value: getStatValue('page_views'),
      icon: Eye,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      change: "+12%"
    },
    {
      title: "Reservas de Tours",
      value: getStatValue('tour_bookings'),
      icon: Calendar,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
      change: "+8%"
    },
    {
      title: "Consultas",
      value: getStatValue('contact_inquiries'),
      icon: Mail,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
      change: "+5%"
    },
    {
      title: "Newsletter",
      value: getStatValue('newsletter_signups'),
      icon: TrendingUp,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      change: "+15%"
    }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-muted rounded w-20"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted rounded w-16 mb-2"></div>
              <div className="h-3 bg-muted rounded w-12"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {statsCards.map((stat, index) => (
        <Card key={index} className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent to-muted/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {stat.title}
            </CardTitle>
            <div className={`p-2 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold">
              {stat.value.toLocaleString()}
            </div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <span className="text-emerald-600 font-medium">{stat.change}</span>
              <span>vs. mes anterior</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DashboardStats;